import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { Steve } from '../../components/Steve';
import { VuMeter } from '../../components/VuMeter';
import { S, t, tList, tPairs, type Leaf } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines } from '../../state/derived';
import { wordForSkin } from '../../state/secrets';
import type { Skin } from '../../state/types';
import {
  INDEX,
  SUGGESTIONS_SHOWN,
  highlight,
  lookup,
  sayFor,
  suggest,
  type IndexHit,
} from './index-table';
import styles from './search.module.css';

/** La chaîne du message flottant, par verdict de recherche. */
const SAY = {
  empty: 'say_empty',
  exact: 'say_exact',
  no_steve: 'say_no_steve',
  same: 'say_same',
} as const;

/** Les cinq onglets. Deux sont volontairement vides. */
type Tab = 'all' | 'img' | 'vid' | 'forum' | 'aqua' | 'more';

/** Les rayons cachés derrière « Plus ». Tous mènent au même poisson, ou à rien. */
const MORE = ['news', 'books', 'shop', 'flights'] as const;
type More = (typeof MORE)[number];
const TABS: readonly Tab[] = ['all', 'img', 'vid', 'forum', 'aqua'];

/** Huit pages sur grand écran, cinq sous 480 px — le mot-symbole étiré déborde. */
const PAGES = 8;
const PAGES_NARROW = 5;

/**
 * Les douze vignettes de l'onglet Images.
 *
 * Ce sont **douze fois le même dessin**, retourné, pivoté, recadré ou agrandi.
 * C'est toute la blague de l'onglet, et la note de bas de grille la dit sans
 * l'expliquer : « Toutes les images représentent le même poisson. Il n'y en a
 * qu'un. »
 */
const SHOTS: readonly { transform: string; background: string }[] = [
  { transform: 'scale(0.5)', background: '#f1f3f4' },
  { transform: 'scale(0.5) scaleX(-1)', background: '#fff' },
  { transform: 'scale(0.55) rotate(-8deg)', background: '#f1f3f4' },
  { transform: 'scale(0.72)', background: '#fff' },
  { transform: 'scale(0.5) rotate(6deg)', background: '#eaf2fb' },
  { transform: 'scale(0.42)', background: '#f1f3f4' },
  { transform: 'scale(0.5)', background: '#fff' },
  { transform: 'scale(0.62) scaleX(-1) rotate(4deg)', background: '#f6f1ea' },
  { transform: 'scale(0.86)', background: '#f1f3f4' },
  { transform: 'scale(0.5) rotate(180deg)', background: '#fff' },
  { transform: 'scale(0.58) rotate(-3deg)', background: '#f1f3f4' },
  { transform: 'scale(0.95) translateX(-14%)', background: '#fff' },
];

/**
 * Skin 11 — Glougle : une page de résultats de recherche.
 *
 * **C'est le seul skin qui parle des autres.** Il indexe les dix autres
 * interfaces : la bonne requête fait apparaître une carte de résultat exact, et
 * un clic bascule le site dessus. Il est donc à la fois un skin et le plan du
 * site caché.
 *
 * Rien n'explique le mécanisme, et rien ne doit l'expliquer — ce serait un texte
 * de mode d'emploi, ce que le brief interdit partout. La seule piste est
 * l'autocomplétion, où six amorces remontent en tête dès qu'on tape « steve ».
 *
 * Le moteur s'appelle Glougle et son mot-symbole est **entièrement orange** : ce
 * n'est pas l'habillage d'un service existant, mais un moteur inventé qui
 * emprunte les conventions du genre — fil d'Ariane au-dessus du titre, titre
 * bleu, extrait gris, panneau de connaissance à droite.
 */
export function SearchSkin(): ReactNode {
  const { lang, seen, meals, draft } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();
  const guestbook = useGuestbookLines();

  const suggestions = tList(S.search.search.suggestions, lang);
  /*
   * Les six amorces, dans la langue servie.
   *
   * Elles doivent correspondre **mot pour mot** à des entrées de `suggestions`,
   * sinon le tri ne les reconnaît pas et elles ne remontent jamais en tête —
   * c'est-à-dire que la seule piste vers l'index disparaît. C'était le cas en
   * anglais, où la liste dit « steve in 3d » et non « steve en 3d ».
   */
  const primers = tList(S.search.search.skin_suggestions, lang);

  const [query, setQuery] = useState(() => t(S.search.search.default_query, lang));
  const [submitted, setSubmitted] = useState(() => t(S.search.search.default_query, lang));
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const [tab, setTab] = useState<Tab>('all');
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreKind, setMoreKind] = useState<More>('news');
  const [toolsOpen, setToolsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [ask, setAsk] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hit = useMemo(() => lookup(submitted), [submitted]);
  const shown = useMemo(() => suggest(query, suggestions, primers), [query, suggestions, primers]);
  const say = sayFor(submitted, hit);

  const questions = S.search.paa.items;
  const files = tPairs(S.search.images.files, lang);

  const run = (value: string): void => {
    setQuery(value);
    setSubmitted(value);
    setOpen(false);
    setCursor(-1);
    setPage(1);
    setTab('all');
  };

  /** Bascule le site sur le skin trouvé. C'est la récompense de la recherche. */
  const goTo = (skin: Skin): void => {
    actions.setSkin(skin);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    // Les flèches parcourent le menu : sans elles, il n'est atteignable qu'à la
    // souris, et c'est le seul chemin vers l'index.
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setCursor((current) => {
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = current + step;
        if (next < 0) return shown.length - 1;
        return next >= shown.length ? 0 : next;
      });
      return;
    }
    if (event.key === 'Enter') {
      run(cursor >= 0 ? (shown[cursor] ?? query) : query);
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
      setCursor(-1);
    }
  };

  return (
    // Le menu se ferme au clic hors du champ : sinon il masque la carte qui
    // vient d'apparaître juste en dessous.
    <div
      className={styles.root}
      onPointerDown={() => {
        setOpen(false);
      }}
    >
      <header className={styles.head}>
        <div
          className={styles.searchRow}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
        >
          <button
            type="button"
            className={styles.wordmark}
            onClick={() => {
              run(t(S.search.search.default_query, lang));
            }}
          >
            {t(S.search.brand.name, lang)}
          </button>

          <div className={styles.barHolder}>
            <div className={cx(styles.bar, open && shown.length > 0 && styles.barOpen)}>
              <label className={styles.srOnly} htmlFor="glougle-q">
                {t(S.search.search.submit, lang)}
              </label>
              <input
                id="glougle-q"
                ref={inputRef}
                type="text"
                className={styles.input}
                value={query}
                autoComplete="off"
                role="combobox"
                aria-expanded={open}
                aria-controls="glougle-suggest"
                onChange={(event) => {
                  setQuery(event.currentTarget.value);
                  setOpen(true);
                  setCursor(-1);
                }}
                onFocus={() => {
                  setOpen(true);
                }}
                onKeyDown={onKeyDown}
              />
              {query !== '' && (
                <button
                  type="button"
                  className={styles.clear}
                  aria-label={t(S.search.search.clear, lang)}
                  onClick={() => {
                    setQuery('');
                    inputRef.current?.focus();
                  }}
                >
                  ✕
                </button>
              )}
              <span className={styles.sep} aria-hidden="true" />
              <button
                type="button"
                className={styles.magnifier}
                aria-label={t(S.search.search.submit, lang)}
                onClick={() => {
                  run(query);
                }}
              >
                ⌕
              </button>
            </div>

            {open && shown.length > 0 && (
              <ul className={styles.suggest} id="glougle-suggest" role="listbox">
                {shown.map((item, index) => (
                  <li key={item}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === cursor}
                      className={cx(styles.suggestItem, index === cursor && styles.suggestOn)}
                      onClick={() => {
                        run(item);
                      }}
                    >
                      <span className={styles.suggestGlyph} aria-hidden="true">
                        ⌕
                      </span>
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <span className={styles.avatar} aria-hidden="true">
            {t(S.search.brand.avatar, lang)}
          </span>
        </div>

        {/*
          « Plus » et « Outils » vivent hors du `<nav>`, et ce n'est pas un
          détail de mise en page : la barre d'onglets défile horizontalement
          (`overflow-x: auto`), et un conteneur qui défile **rogne ses enfants
          en position absolue**. Le menu déroulant y était découpé, quel que
          soit son `z-index`. Ce ne sont d'ailleurs pas des onglets : l'un ouvre
          un menu, l'autre déplie une barre.
        */}
        <div className={styles.tabsRow}>
          <nav className={styles.tabs} aria-label={t(S.search.tabs.all, lang)}>
            {TABS.map((id) => (
              <button
                key={id}
                type="button"
                className={cx(styles.tab, tab === id && styles.tabOn)}
                onClick={() => {
                  setTab(id);
                }}
              >
                {t(S.search.tabs[id], lang)}
              </button>
            ))}
          </nav>
          <div className={styles.moreHolder}>
            <button
              type="button"
              className={cx(styles.tab, moreOpen && styles.tabOn)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              onClick={() => {
                setMoreOpen((open) => !open);
              }}
            >
              {t(S.search.tabs.more, lang)}
            </button>
            {moreOpen && (
              <div className={styles.moreMenu} role="menu">
                {MORE.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    role="menuitem"
                    className={styles.moreItem}
                    onClick={() => {
                      setMoreKind(kind);
                      setTab('more');
                      setMoreOpen(false);
                    }}
                  >
                    {t(S.search.more_menu[kind], lang)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className={cx(styles.tab, styles.tabTools, toolsOpen && styles.tabOn)}
            aria-pressed={toolsOpen}
            onClick={() => {
              setToolsOpen((open) => !open);
            }}
          >
            {t(S.search.tabs.tools, lang)}
          </button>
        </div>

        {toolsOpen && (
          <p className={styles.toolsLine} role="status">
            {t(S.search.tools_line, lang)}
          </p>
        )}
      </header>

      <p className={styles.stats} role="status">
        {tab === 'img'
          ? t(S.search.stats.img, lang, { n: SHOTS.length })
          : tab === 'forum'
            ? t(S.search.stats.forum, lang, { n: guestbook.length })
            : t(hit ? S.search.stats.exact : S.search.stats.all, lang)}
      </p>

      <div className={styles.columns}>
        <main className={styles.results}>
          {tab === 'all' && (
            <>
              {hit && <ExactCard hit={hit} lang={lang} onOpen={goTo} />}

              <section className={styles.card}>
                <p className={styles.cardLabel}>{t(S.search.featured.label, lang)}</p>
                {/* Les paroles sont la source primaire : citées verbatim, avec
                    les termes de la requête en gras. */}
                <p className={styles.quote}>
                  {highlight(t(S.search.featured.quote, lang), submitted).map((piece, index) =>
                    piece.hit ? <strong key={index}>{piece.text}</strong> : <span key={index}>{piece.text}</span>,
                  )}
                </p>
                <div className={styles.quoteSource}>
                  <span className={styles.favicon} aria-hidden="true" />
                  <span>
                    <span className={styles.link}>{t(S.search.featured.source, lang)}</span>
                    <span className={styles.meta}>{t(S.search.featured.meta, lang)}</span>
                  </span>
                </div>
              </section>

              <Organic
                bread={t(S.search.results.wiki_bread, lang)}
                url={t(S.search.results.wiki_url, lang)}
                title={t(S.search.results.wiki_title, lang)}
                snippet={t(S.search.results.wiki_snippet, lang)}
                onOpen={() => {
                  goTo('wiki');
                }}
              >
                <div className={styles.sitelinks}>
                  {tPairs(S.search.results.wiki_sitelinks, lang).map((pair) => (
                      <div key={pair[0]}>
                        <button
                          type="button"
                          className={styles.link}
                          onClick={() => {
                            goTo('wiki');
                          }}
                        >
                          {pair[0]}
                        </button>
                      <p className={styles.sitelinkText}>{pair[1]}</p>
                    </div>
                  ))}
                </div>
              </Organic>

              <Organic
                bread={t(S.search.results.aqua_bread, lang)}
                url={t(S.search.results.aqua_url, lang)}
                title={t(S.search.results.aqua_title, lang)}
                snippet={t(S.search.results.aqua_snippet, lang, { n: meals })}
                onOpen={() => {
                  goTo('aqua');
                }}
              />

              <Organic
                bread={t(S.search.results.forum_bread, lang)}
                url={t(S.search.results.forum_url, lang)}
                title={t(S.search.results.forum_title, lang, { n: 47 })}
                snippet={t(S.search.results.forum_snippet, lang)}
                onOpen={() => {
                  setTab('forum');
                }}
              />

              <Organic
                bread={t(S.search.results.inst_bread, lang)}
                url={t(S.search.results.inst_url, lang)}
                title={t(S.search.results.inst_title, lang)}
                snippet={t(S.search.results.inst_snippet, lang)}
                onOpen={() => {
                  goTo('wiki');
                }}
              />

              <section className={styles.card}>
                <h2 className={styles.blockTitle}>{t(S.search.paa.heading, lang)}</h2>
                <ul className={styles.paa}>
                  {questions.map((item, index) => (
                    <li key={item.q_fr} className={styles.paaItem}>
                      <button
                        type="button"
                        className={styles.paaQuestion}
                        aria-expanded={ask === index}
                        aria-controls={`paa-${String(index)}`}
                        onClick={() => {
                          setAsk((current) => (current === index ? null : index));
                        }}
                      >
                        <span>{lang === 'fr' ? item.q_fr : item.q_en}</span>
                        <span aria-hidden="true">{ask === index ? '▴' : '▾'}</span>
                      </button>
                      {ask === index && (
                        <div className={styles.paaAnswer} id={`paa-${String(index)}`}>
                          <p className={styles.paragraph}>{lang === 'fr' ? item.a_fr : item.a_en}</p>
                          <p className={styles.meta}>{lang === 'fr' ? item.src_fr : item.src_en}</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>

              <section className={styles.card}>
                <div className={styles.playerHead}>
                  <span className={styles.favicon} aria-hidden="true" />
                  <span>
                    <span className={styles.playerTitle}>{t(S.search.player.title, lang)}</span>
                    <span className={styles.meta}>
                      {t(S.search.player.meta, lang, { state: mixer.stateLabel })}
                    </span>
                  </span>
                </div>

                <VuMeter className={styles.vu} barClassName={styles.vuBar} />

                <div className={styles.pads}>
                  {mixer.pads.map((pad) => (
                    <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                      <span>{pad.label}</span>
                      <span className={styles.padTime}>{pad.timeLabel}</span>
                    </button>
                  ))}
                </div>

                {mixer.sliders.map((slider) => (
                  <div key={slider.key} className={styles.slider}>
                    <label className={styles.sliderLabel} htmlFor={`glougle-${slider.key}`}>
                      {slider.label}
                    </label>
                    <input
                      id={`glougle-${slider.key}`}
                      type="range"
                      className={styles.sliderInput}
                      min={slider.min}
                      max={slider.max}
                      step={slider.step}
                      value={slider.value}
                      onChange={(event) => {
                        slider.onChange(event.currentTarget.valueAsNumber);
                      }}
                    />
                    <output className={styles.sliderValue} htmlFor={`glougle-${slider.key}`}>
                      {slider.valueLabel}
                    </output>
                  </div>
                ))}

                <div className={styles.chips}>
                  <button type="button" className={styles.chip} onClick={actions.toggleAudio}>
                    {audio.label}
                  </button>
                  <button type="button" className={styles.chip} onClick={mixer.onToggleHold}>
                    {mixer.holdLabel}
                  </button>
                  <button type="button" className={styles.chip} onClick={mixer.onStop}>
                    {t(S.search.player.mute, lang)}
                  </button>
                  {mixer.presets.map((preset) => (
                    <button key={preset.name} type="button" className={styles.chip} onClick={preset.onSelect}>
                      {preset.label}
                    </button>
                  ))}
                  <button type="button" className={styles.chip} onClick={mixer.onToggleWobble}>
                    {mixer.wobbleLabel}
                  </button>
                </div>
                <p className={styles.meta} role="status">
                  {audio.hint}
                </p>
              </section>

              <section className={styles.card}>
                <h2 className={styles.blockTitle}>{t(S.search.related.heading, lang)}</h2>
                <div className={styles.relatedGrid}>
                  {tPairs(S.search.related.items, lang).map((pair) => (
                      <button
                        key={`${pair[0] ?? ''}${pair[1] ?? ''}`}
                        type="button"
                        className={styles.related}
                        onClick={() => {
                          run(`${pair[0] ?? ''} ${pair[1] ?? ''}`);
                        }}
                      >
                        <span className={styles.relatedGlyph} aria-hidden="true">
                          ⌕
                        </span>
                        <span>
                          {pair[0]} <strong>{pair[1]}</strong>
                        </span>
                      </button>
                  ))}
                </div>
              </section>

              <Pagination page={page} lang={lang} onPage={setPage} />
            </>
          )}

          {tab === 'img' && (
            <>
              <div className={styles.imageGrid}>
                {SHOTS.map((shot, index) => {
                  const file = files[index] ?? ['steve.png', ''];
                  return (
                    <figure key={file[0]} className={styles.shot}>
                      <div className={styles.shotBox} style={{ background: shot.background }}>
                        <div
                          className={styles.shotScale}
                          style={{ transform: shot.transform }}
                          aria-hidden="true"
                        >
                          <Steve bobDuration="0s" />
                        </div>
                      </div>
                      <figcaption className={styles.shotName}>
                        <span className={styles.shotFile}>{file[0]}</span>
                        <span className={styles.meta}>{file[1]}</span>
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
              <p className={styles.imageNote}>{t(S.search.images.note, lang)}</p>
            </>
          )}

          {tab === 'vid' && (
            <Empty
              title={t(S.search.empty.vid_title, lang)}
              body={t(S.search.empty.vid_body, lang)}
              back={t(S.search.empty.back, lang)}
              lucky={t(S.search.empty.lucky, lang)}
              onBack={() => {
                setTab('all');
              }}
              onLucky={() => {
                actions.cycleSkin(0);
              }}
            />
          )}

          {tab === 'more' && (
            <Empty
              title={t(S.search.empty.more_title, lang, {
                rayon: t(S.search.more_menu[moreKind], lang),
              })}
              body={t(S.search.empty.more_body, lang)}
              back={t(S.search.empty.back, lang)}
              lucky={t(S.search.empty.lucky, lang)}
              onBack={() => {
                setTab('all');
              }}
              onLucky={() => {
                actions.cycleSkin(0);
              }}
            />
          )}

          {tab === 'aqua' && (
            <Empty
              title={t(S.search.empty.aqua_title, lang)}
              body={t(S.search.empty.aqua_body, lang)}
              back={t(S.search.empty.back, lang)}
              lucky={t(S.search.empty.lucky, lang)}
              onBack={() => {
                setTab('all');
              }}
              onLucky={() => {
                actions.cycleSkin(0);
              }}
            />
          )}

          {tab === 'forum' && (
            <>
              <ul className={styles.threads}>
                {guestbook.map((entry, index) => (
                  <li key={entry.id} className={styles.card}>
                    <p className={styles.bread}>{t(S.search.results.forum_bread, lang)}</p>
                    <button
                      type="button"
                      className={styles.resultTitle}
                      onClick={() => {
                        setTab('all');
                      }}
                    >
                      {entry.text}
                    </button>
                    <p className={styles.meta}>
                      {entry.who} · {tList(S.search.forum_tab.meta, lang)[index % 5]}
                    </p>
                  </li>
                ))}
              </ul>
              <div className={styles.compose}>
                <input
                  type="text"
                  className={styles.composeInput}
                  value={draft}
                  placeholder={t(S.search.forum_tab.placeholder, lang)}
                  aria-label={t(S.search.forum_tab.placeholder, lang)}
                  onChange={(event) => {
                    actions.setDraft(event.currentTarget.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') actions.postEntry();
                  }}
                />
                <button type="button" className={styles.chip} onClick={actions.postEntry}>
                  {t(S.search.forum_tab.post, lang)}
                </button>
              </div>
            </>
          )}
        </main>

        <aside className={styles.panel}>
          <button
            type="button"
            className={styles.panelImage}
            aria-label={t(S.search.knowledge.title, lang)}
            onClick={actions.bump}
          >
            <span className={styles.panelScale} aria-hidden="true">
              <Steve bobDuration="0s" />
            </span>
          </button>
          <h2 className={styles.panelTitle}>{t(S.search.knowledge.title, lang)}</h2>
          <p className={styles.panelKind}>{t(S.search.knowledge.kind, lang)}</p>
          <p className={styles.paragraph}>{t(S.search.knowledge.summary, lang)}</p>
          <dl className={styles.facts}>
            <Fact k={t(S.search.knowledge.colour, lang)} v={t(S.search.knowledge.colour_v, lang)} />
            <Fact k={t(S.search.knowledge.arms, lang)} v="2" />
            <Fact k={t(S.search.knowledge.legs, lang)} v="2" />
            <Fact k={t(S.search.knowledge.eyes, lang)} v={t(S.search.knowledge.eyes_v, lang)} />
            <Fact k={t(S.search.knowledge.size, lang)} v={t(S.search.knowledge.size_v, lang)} />
            <Fact k={t(S.search.knowledge.smell, lang)} v={t(S.search.knowledge.smell_v, lang)} />
            <Fact
              k={t(S.search.knowledge.drawing, lang)}
              v={
                <a
                  className={styles.link}
                  href="https://www.instagram.com/vigzvigz/?hl=fr"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Vigz
                </a>
              }
            />
            <Fact
              k={t(S.search.knowledge.music, lang)}
              v={
                <a
                  className={styles.link}
                  href="https://www.youtube.com/@tomomp3"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Tomo
                </a>
              }
            />
            <Fact k={t(S.search.knowledge.views, lang)} v={String(seen)} />
          </dl>
          <div className={styles.chips}>
            <button type="button" className={styles.chip} onClick={actions.bump}>
              {t(S.search.knowledge.look, lang)}
            </button>
            <button
              type="button"
              className={styles.chip}
              onClick={() => {
                actions.cycleSkin(0);
              }}
            >
              {t(S.search.knowledge.another, lang)}
            </button>
          </div>

          <h3 className={styles.blockTitle}>{t(S.search.knowledge.also_heading, lang)}</h3>
          <ul className={styles.also}>
            {tPairs(S.search.knowledge.also, lang).map((pair) => (
                <li key={pair[0]}>
                  <button
                    type="button"
                    className={styles.link}
                    onClick={() => {
                      run(pair[0] ?? '');
                    }}
                  >
                    {pair[0]}
                  </button>
                  <span className={styles.meta}>{pair[1]}</span>
                </li>
            ))}
          </ul>
        </aside>
      </div>

      <p className={styles.say} role="status">
        {t(S.search.search[SAY[say]], lang)}
      </p>

      <footer className={styles.footer}>
        <p>{t(S.search.footer.credits, lang)}</p>
        <p>{t(S.common.egg_hint, lang)}</p>
      </footer>
    </div>
  );
}

/** Une ligne de fiche du panneau de connaissance. */
function Fact({ k, v }: { readonly k: string; readonly v: ReactNode }): ReactNode {
  return (
    <div className={styles.fact}>
      <dt className={styles.factKey}>{k}</dt>
      <dd className={styles.factValue}>{v}</dd>
    </div>
  );
}

interface ExactProps {
  readonly hit: IndexHit;
  readonly lang: 'fr' | 'en';
  readonly onOpen: (skin: Skin) => void;
}

/**
 * La carte de résultat exact.
 *
 * Elle a l'anatomie d'un résultat organique, un fond à peine plus chaud, et un
 * **bouton d'action explicite** — « Ouvrir le bac », « Insérer une pièce ». Ce
 * bouton n'est pas décoratif : c'est lui qui porte l'information, parce qu'un
 * fond `#fffdf9` seul ne distingue rien pour qui ne voit pas les couleurs.
 */
function ExactCard({ hit, lang, onOpen }: ExactProps): ReactNode {
  const word = wordForSkin(hit.skin);

  return (
    <section className={cx(styles.card, styles.exact)} data-exact={hit.skin}>
      <p className={styles.bread}>
        <span className={styles.favicon} aria-hidden="true" />
        {hit.bread[lang]}
      </p>
      <p className={styles.url}>{hit.url[lang]}</p>
      <button
        type="button"
        className={styles.resultTitle}
        onClick={() => {
          onOpen(hit.skin);
        }}
      >
        {hit.title[lang]}
      </button>
      <p className={styles.snippet}>{hit.snippet[lang]}</p>
      <div className={styles.ctaRow}>
        <button
          type="button"
          className={cx(styles.chip, styles.cta)}
          onClick={() => {
            onOpen(hit.skin);
          }}
        >
          {hit.cta[lang]}
        </button>
        {/*
          Le mot à taper, posé contre le bouton qui fait la même chose — comme
          un raccourci clavier à droite d'une entrée de menu. Sa place suffit à
          l'expliquer : rien à écrire, et le libellé complet ne sert qu'à ceux
          qui n'ont pas la mise en page, via `aria-label`.
        */}
        {word !== null && (
          <span
            className={styles.shortcut}
            aria-label={t(S.search.shortcut, lang, { word: word.toUpperCase() })}
          >
            {/*
              Sélecteur de présentation textuelle après le glyphe : sans lui,
              la plupart des systèmes le rendent en émoji couleur, qui jure
              avec une interface qui n'en contient aucun autre.
            */}
            <span aria-hidden="true">{'\u2328\uFE0E'} {word.toUpperCase()}</span>
          </span>
        )}
      </div>
    </section>
  );
}

interface OrganicProps {
  readonly bread: string;
  readonly url: string;
  readonly title: string;
  readonly snippet: string;
  readonly onOpen: () => void;
  readonly children?: ReactNode;
}

/** Un résultat organique : fil d'Ariane, URL, titre bleu, extrait gris. */
function Organic({ bread, url, title, snippet, onOpen, children }: OrganicProps): ReactNode {
  return (
    <section className={styles.card}>
      <p className={styles.bread}>
        <span className={styles.favicon} aria-hidden="true" />
        {bread}
      </p>
      <p className={styles.url}>{url}</p>
      <button type="button" className={styles.resultTitle} onClick={onOpen}>
        {title}
      </button>
      <p className={styles.snippet}>{snippet}</p>
      {children}
    </section>
  );
}

interface EmptyProps {
  readonly title: string;
  readonly body: string;
  readonly back: string;
  readonly lucky: string;
  readonly onBack: () => void;
  readonly onLucky: () => void;
}

/** Un onglet volontairement vide, avec sa justification. */
function Empty({ title, body, back, lucky, onBack, onLucky }: EmptyProps): ReactNode {
  return (
    <section className={styles.card}>
      <h2 className={styles.blockTitle}>{title}</h2>
      <p className={styles.paragraph}>{body}</p>
      <div className={styles.chips}>
        <button type="button" className={styles.chip} onClick={onBack}>
          {back}
        </button>
        <button type="button" className={styles.chip} onClick={onLucky}>
          {lucky}
        </button>
      </div>
    </section>
  );
}

/**
 * La pagination : le mot-symbole étiré, `Gl` + huit `o` + `gle`.
 *
 * Elle ne change rien, et le dit. Sous 480 px elle tombe à cinq `o` : le
 * mot-symbole étiré à huit déborde d'un écran de téléphone.
 */
function Pagination({
  page,
  lang,
  onPage,
}: {
  readonly page: number;
  readonly lang: 'fr' | 'en';
  readonly onPage: (page: number) => void;
}): ReactNode {
  const pages = Array.from({ length: PAGES }, (_, index) => index + 1);
  return (
    <nav className={styles.pager} aria-label={t(S.search.pagination.note, lang, { n: page })}>
      <span className={styles.pagerWord} aria-hidden="true">
        Gl
      </span>
      {pages.map((n) => (
        <button
          key={n}
          type="button"
          className={cx(
            styles.pagerO,
            n === page && styles.pagerOn,
            n > PAGES_NARROW && styles.pagerExtra,
          )}
          // Le `o` est décoratif : sans ce libellé, le bouton s'annonce comme
          // un simple numéro, et le mot-symbole étiré n'a plus de sens au son.
          aria-label={t(S.search.pagination.page, lang, { n })}
          aria-current={n === page ? 'page' : undefined}
          onClick={() => {
            onPage(n);
          }}
        >
          <span aria-hidden="true">o</span>
          <span className={styles.pagerNum}>{n}</span>
        </button>
      ))}
      <span className={styles.pagerWord} aria-hidden="true">
        gle
      </span>
      <p className={styles.pagerNote}>{t(S.search.pagination.note, lang, { n: page })}</p>
    </nav>
  );
}

/** Réexporté pour les tests : la table est la fonctionnalité, pas un détail. */
export { INDEX, SUGGESTIONS_SHOWN };
export type { Leaf };
